import type React from "react";
import { useState } from "react";
import {
  Box,
  Drawer,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  ListItemText,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";
import type {
  CategoryResponse,
  DesignResponse,
  UploadedFile,
} from "../../types/figma";
import CustomTextField from "../../../../components/common/Textfield";
import { GalleryAddIcon } from "../../../../components/icons";
import { toast } from "react-toastify";

interface CreateDesignPayload {
  name: string;
  figma_link: string;
  category: number;
  design_type: "design" | "prototype" | "design_prototype";
  design_image?: File;
  description?: string;
}

interface AddDesignSidebarProps {
  open: boolean;
  onClose: () => void;
  categories: CategoryResponse[];
  onCreateCategory: () => void;
  onSave: (data: CreateDesignPayload) => Promise<DesignResponse>;
}

export const AddDesignSidebar: React.FC<AddDesignSidebarProps> = ({
  open,
  onClose,
  categories,
  onCreateCategory,
  onSave,
}) => {
  const [formData, setFormData] = useState<{
    name: string;
    category: number | "";
    figma_link: string;
    design_type: string;
  }>({
    name: "",
    category: "",
    figma_link: "",
    design_type: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    const newFiles: UploadedFile[] = Array.from(files).map((file) => ({
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file),
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };
  const handleSave = async () => {
    try {
      setIsSaving(true);

      await onSave({
        name: formData.name,
        figma_link: formData.figma_link,
        category: Number(formData.category),
        design_type: formData.design_type as CreateDesignPayload["design_type"],
        design_image: uploadedFiles[0]?.file,
      });

      toast.success("Design saved successfully ");

      setFormData({
        name: "",
        category: "",
        design_type: "",
        figma_link: "",
      });

      setUploadedFiles([]);
      onClose();
    } catch (error) {
      console.error("Failed to save design:", error);
      toast.error("Failed to save design");
    } finally {
      setIsSaving(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 420,
          p: 4,
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 4,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Add new design
              </Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <CustomTextField
                fullWidth
                placeholder="Design name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                variant="outlined"
              />
              <CustomTextField
                fullWidth
                placeholder="Figma link "
                value={formData.figma_link}
                onChange={(e) =>
                  setFormData({ ...formData, figma_link: e.target.value })
                }
                variant="outlined"
              />

              <FormControl fullWidth>
                <Select<number | "">
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      category: e.target.value as number,
                    })
                  }
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography color="text.secondary">
                          Select category
                        </Typography>
                      );
                    }

                    const category = categories.find((c) => c.id === selected);

                    return category?.name ?? "Unknown category";
                  }}
                >
                  <MenuItem onClick={onCreateCategory}>
                    <ListItemText
                      primary="Create new Category"
                      sx={{ fontWeight: 900 }}
                    />
                  </MenuItem>
                  <Divider />
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <ListItemText primary={category.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <Select
                  value={formData.design_type}
                  onChange={(e) =>
                    setFormData({ ...formData, design_type: e.target.value })
                  }
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography color="text.secondary">
                          Design type
                        </Typography>
                      );
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="design">Design</MenuItem>
                  <MenuItem value="prototype">Prototype</MenuItem>
                  <MenuItem value="prototype_design">
                    Prototype & Design
                  </MenuItem>
                </Select>
              </FormControl>
              <input
                type="file"
                hidden
                multiple
                id="design-upload"
                onChange={(e) => handleFiles(e.target.files)}
              />

              <Box
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() =>
                  document.getElementById("design-upload")?.click()
                }
                sx={{
                  border: "1px solid #ACACAC",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "white",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#999",
                    backgroundColor: "#FAFAFA",
                  },
                }}
              >
                <GalleryAddIcon sx={{ fontSize: 48, color: "#CCC", mb: 2 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Upload or drag and drop file
                </Typography>
                <Button
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById("design-upload")?.click();
                  }}
                >
                  Browse files
                </Button>
              </Box>

              {uploadedFiles.map((file, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.3 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #ACACAC",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        backgroundColor: "#4CAF50",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "white",
                          fontWeight: 700,

                          fontSize: "0.75rem",
                        }}
                      >
                        {file.name.split(".").pop()?.toUpperCase()}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, fontStyle: "bold" }}
                      >
                        {file.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#C5C5C5",
                          fontWeight: 700,
                          fontStyle: "bold",
                        }}
                      >
                        {formatFileSize(file.size)} · Done
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() =>
                        setUploadedFiles((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </motion.div>
              ))}

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    mt: "auto",
                    py: 1.5,
                    width: "119px",
                    borderRadius: "8px",
                  }}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Drawer>
  );
};
